import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatMenuModule } from '@angular/material/menu';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AyudasService, Ayuda } from '../../services/ayudas.service';
import { catchError, finalize } from 'rxjs/operators';
import { of } from 'rxjs';
import { FeathericonsModule } from '../../icons/feathericons/feathericons.module';

@Component({
    selector: 'app-ayudas',
    standalone: true,
    imports: [
        CommonModule,
        MatCardModule,
        MatButtonModule,
        MatProgressSpinnerModule,
        MatIconModule,
        MatSnackBarModule,
        MatMenuModule,
        FeathericonsModule,
    ],
    templateUrl: './ayudas.component.html',
    styleUrl: './ayudas.component.scss',
})
export class AyudasComponent implements OnInit {
    @ViewChild('videoDialog') videoDialogRef!: ElementRef<HTMLDialogElement>;

    ayudas: Ayuda[] = [];
    /** Documentos (PDF/manuales) para la sección "Manuales y documentos disponibles" */
    documentos: Ayuda[] = [];
    /** Videos (tutoriales) para la sección "Tutoriales disponibles" */
    videos: Ayuda[] = [];
    isLoading = false;
    /** Ayuda de tipo video actualmente mostrada en el modal */
    videoEnReproduccion: Ayuda | null = null;

    constructor(
        private ayudasService: AyudasService,
        private snackBar: MatSnackBar,
        private sanitizer: DomSanitizer
    ) {}

    ngOnInit(): void {
        this.loadAyudas();
    }

    loadAyudas(): void {
        this.isLoading = true;
        this.ayudasService
            .getList()
            .pipe(
                catchError(() => {
                    this.snackBar.open('Error al cargar las ayudas', 'Cerrar', {
                        duration: 5000,
                        panelClass: ['error-snackbar'],
                    });
                    return of({ success: false, data: [] });
                }),
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe((res) => {
                if (res.success && res.data) {
                    const activas = res.data
                        .filter((a) => a.activo)
                        .sort((a, b) => a.orden - b.orden);
                    this.ayudas = activas;
                    this.documentos = activas.filter((a) => a.tipo === 'documento');
                    this.videos = activas.filter((a) => a.tipo === 'video');
                }
            });
    }

    getYouTubeVideoId(url: string): string | null {
        if (!url) return null;
        const patterns = [
            /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
            /youtube\.com\/.*[?&]v=([^&\n?#]+)/,
        ];
        for (const pattern of patterns) {
            const match = url.match(pattern);
            if (match && match[1]) return match[1];
        }
        return null;
    }

    getYouTubeEmbedUrl(url: string): SafeResourceUrl | null {
        const videoId = this.getYouTubeVideoId(url);
        if (!videoId) return null;
        return this.sanitizer.bypassSecurityTrustResourceUrl(
            `https://www.youtube.com/embed/${videoId}`
        );
    }

    downloadAyuda(ayuda: Ayuda): void {
        if (ayuda.tipo !== 'documento') return;
        this.ayudasService.downloadFile(ayuda.id).subscribe({
            next: (blob) => {
                const ext = ayuda.tipoDocumento || 'pdf';
                const name = `${ayuda.titulo.replace(/[^a-zA-Z0-9-_]/g, '_')}.${ext}`;
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = name;
                a.click();
                URL.revokeObjectURL(url);
            },
            error: () => {
                this.snackBar.open('Error al descargar el archivo', 'Cerrar', {
                    duration: 5000,
                    panelClass: ['error-snackbar'],
                });
            },
        });
    }

    openVideoModal(ayuda: Ayuda): void {
        if (ayuda.tipo !== 'video') return;
        this.videoEnReproduccion = ayuda;
        this.videoDialogRef?.nativeElement?.showModal?.();
    }

    closeVideoModal(event: Event): void {
        event?.preventDefault?.();
        this.videoEnReproduccion = null;
        this.videoDialogRef?.nativeElement?.close?.();
    }

    openVideoInNewTab(ayuda: Ayuda): void {
        if (ayuda.tipo !== 'video' || !ayuda.urlOrPath) return;
        window.open(ayuda.urlOrPath, '_blank', 'noopener,noreferrer');
    }

    trackByAyudaId(_index: number, ayuda: Ayuda): string {
        return ayuda.id;
    }
}
